<script setup>
import { computed, ref, watch } from "vue";
import { useTagStore } from "@/stores/useTagStore";
import { useUserStore } from "@/stores/useUserStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import UserCardModal from "@/components/User/UserCardModal.vue";
import EditTagForm from "@/components/Tag/EditTagForm.vue";
import DeleteTagForm from "@/components/Tag/DeleteTagForm.vue";

// 1. Não esqueça de importar a instância da API no topo do seu <script setup>
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, required: true },
  tagId: { type: Number, required: true },
});

const emit = defineEmits(["close", "tag-updated", "notify"]);

const tagStore = useTagStore();
const userStore = useUserStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);

// Estados para controle do Modal de Usuário
const showUserModal = ref(false);
const selectedUserId = ref(null);

// Estado para controle do EditTagForm
const showEditModal = ref(false);

// Estado para controle do DeleteTagForm
const showDeleteModal = ref(false);

// ── Permissão de edição ──────────────────────────────────────────────────────
const canEdit = computed(() => {
  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;
  if (logged === "sys") return true;
  return tag.value?.createdByUserId === userStore.currentUser?.userId;
});

function openEditModal() {
  tagStore.setIdTag(props.tagId);
  showEditModal.value = true;
}

function handleEditRefresh() {
  tagStore.getTagById(props.tagId);
  emit("tag-updated");
}

function openDeleteModal() {
  tagStore.setIdTag(props.tagId);
  showDeleteModal.value = true;
}

function handleDeleteModalClose() {
  showDeleteModal.value = false;
}

function handleDeleteRefresh() {
  showDeleteModal.value = false;
  emit("tag-updated");
  emit("close");
}

function handleDeleteNotify(payload) {
  emit("notify", payload);
}

// ── Usuário ──────────────────────────────────────────────────────────────────
function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

const tag = computed(() => {
  // se o selectedTag for justamente o da prop, usa ele (vem do GET /tags/{id})
  if (tagStore.selectedTag && tagStore.selectedTag.tagId === props.tagId) {
    return tagStore.selectedTag;
  }
  // fallback: procura na lista paginada
  return tagStore.tags.find((n) => n.tagId === props.tagId) || null;
});

watch(
  () => props.tagId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      await tagStore.getTagById(newId); // agora preenche selectedTag
    } catch (e) {
      console.error("Erro ao carregar tag no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// 2. Função de normalização atualizada para incluir a URL da API
function normalizeAvatarPath(path) {
  if (!path) return "";

  // Se for URL externa (Google/Facebook), retorna direto
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  // Pega a URL base da API e remove o "/api" do final (igual ao Dashboard)
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");

  // Garante que o caminho comece com "/"
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  // Retorna a URL completa: https://api.litterae.com.br/userphotos/foto.jpg
  return `${base}${cleanPath}`;
}

// 3. As funções de busca agora NÃO usam mais pravatar
function getCreatorAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.createdByUserPhotoPath || data.createdByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

function getUpdaterAvatarUrl(data) {
  if (!data) return "";
  const path =
    data.updatedByUserPhotoPath || data.updatedByUser?.photoPath || "";
  if (!path) return "";
  return normalizeAvatarPath(path);
}

// 🔹 Preview chip (palavra única) — espelha SettingsModal
const singleWordPreviewStyle = computed(() => {
  if (!tag.value) return {};
  return {
    backgroundColor: "transparent",
    color: tag.value.specificColor,
    borderRadius: "999px",
    padding: "0.15rem 0.15rem",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1.4",
    verticalAlign: "baseline",
    fontWeight: "600",
    border: "none",
  };
});

// 🔹 Preview trecho com várias palavras — espelha SettingsModal
const multiWordPreviewStyle = computed(() => {
  if (!tag.value) return {};
  return {
    display: "inline-block",
    padding: "1px 3px",
    borderRadius: "0.5rem",
    fontWeight: "400",
    color: "var(--text-dark)",
    backgroundColor: tag.value.specificBackground,
    lineHeight: "1.05",
  };
});
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div
      ref="panelRef"
      class="note-subtype-card-modal-content"
      :style="panelStyle"
    >
      <!-- ── Header ─────────────────────────────────────────────────────── -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">Detalhes da Tag</h3>

        <div class="modal-header-actions">
          <button
            @click="emit('close')"
            type="button"
            class="modal-close-btn"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- ── Conteúdo ───────────────────────────────────────────────────── -->
      <div v-if="tag && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <div class="title-row">
            <h3 class="text-title-bold">
              {{ toTitleCase(tag.tagName) }}
              <span class="title-id">(Id: {{ tag.tagId }})</span>
            </h3>

            <button
              v-if="canEdit"
              type="button"
              class="btn-edit-tag tooltip-bottom"
              data-tooltip="Editar tag"
              @click.stop="openEditModal"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>

            <button
              v-if="canEdit"
              type="button"
              class="btn-delete-tag tooltip-bottom"
              data-tooltip="Apagar tag"
              @click.stop="openDeleteModal"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>

          <div class="meta-grid">
            <!-- ✅ DESCRIÇÃO SEMPRE VISÍVEL -->
            <div class="meta-item meta-item-full">
              <span class="meta-label">Descrição</span>

              <blockquote class="description-quote">
                <span class="description-quote-text">
                  {{ tag.description || "Nenhuma descrição informada." }}
                </span>
              </blockquote>
            </div>

            <div class="meta-item">
              <span class="meta-label">Cores</span>

              <!-- ✅ tudo em UMA LINHA -->
              <span class="meta-value meta-value-colors">
                <div class="color-row">
                  <div
                    class="color-dot"
                    :style="{ backgroundColor: tag.specificColor }"
                    :title="tag.specificColor"
                  ></div>
                  <span class="color-label">Texto</span>
                </div>

                <div class="color-row">
                  <!-- ✅ FUNDO: cor de fundo configurada da tag -->
                  <div
                    class="color-dot"
                    :style="{ backgroundColor: tag.specificBackground }"
                    :title="tag.specificBackground"
                  ></div>
                  <span class="color-label">Fundo</span>
                </div>

                <!-- ── REMOVIDO: linha "Sublinhado" ── -->
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <div class="previews-container">
            <span class="meta-label">Pré-visualização</span>
            <div class="shared-preview-shell">
              <blockquote class="poem-blockquote">
                <p class="poem-line">No silêncio da tarde, a luz repousa,</p>
                <p class="poem-line">sobre a janela antiga e sonhadora;</p>
                <p class="poem-line">
                  e o vento leva,
                  <span :style="singleWordPreviewStyle">manso</span>, a memória,
                </p>
                <p class="poem-line">
                  de um
                  <span :style="multiWordPreviewStyle"
                    >tempo feito de sombra</span
                  >
                  e rosa.
                </p>
              </blockquote>
            </div>
          </div>

          <div class="footer-info">
            <div class="users-row">
              <!-- Criador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(tag.createdByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getCreatorAvatarUrl(tag)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getCreatorAvatarUrl(tag)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">
                    {{ tag.createdByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(tag.createdAt) }}
                  </span>
                </div>
              </div>

              <!-- Atualizador -->
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(tag.updatedByUserId)"
              >
                <div class="chip-avatar">
                  <div
                    v-if="getUpdaterAvatarUrl(tag)"
                    class="chip-avatar-img"
                    :style="{
                      backgroundImage: `url('${getUpdaterAvatarUrl(tag)}')`,
                    }"
                  ></div>
                  <i
                    v-else
                    class="fas fa-user chip-avatar-icon"
                    aria-hidden="true"
                  ></i>
                </div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">
                    {{ tag.updatedByUserName || "Não informado" }}
                  </span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(tag.updatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes...
      </div>

    </div>
  </div>

  <!-- Sub-modais via Teleport.
       Todos são position: fixed e o painel deste card ganha transform ao ser
       arrastado — um ancestral com transform viraria o containing block deles,
       fazendo o overlay cobrir só o painel e acompanhar o arrasto. Fora do
       painel no DOM, seguem ancorados na viewport. -->
  <Teleport to="body">
    <UserCardModal
      v-if="showUserModal"
      :show="showUserModal"
      :userId="selectedUserId"
      @close="closeUserModal"
    />

    <EditTagForm
      v-if="showEditModal"
      @close="showEditModal = false"
      @refresh="handleEditRefresh"
    />
    <DeleteTagForm
      v-if="showDeleteModal"
      @close="handleDeleteModalClose"
      @refresh="handleDeleteRefresh"
      @notify="handleDeleteNotify"
    />
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

/* ✅ container harmonizado */
.note-subtype-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

/* Os botões do cabeçalho continuam sendo clique, não alça */
.modal-header--draggable .modal-close-btn,
.modal-header--draggable .modal-header-actions button {
  cursor: pointer;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* ── Agrupamento dos botões do header ── */
.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Botão Editar (ícone com chip, após o id) ── */
.btn-edit-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--info-border);
  background-color: var(--info-bg);
  color: var(--info-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-edit-tag i {
  font-size: 0.6rem;
}
.btn-edit-tag:hover {
  background-color: var(--info-border);
  transform: translateY(-1px);
}

/* ── Botão Apagar (ícone com chip, após o editar) ── */
.btn-delete-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-text);
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}
.btn-delete-tag i {
  font-size: 0.6rem;
}
.btn-delete-tag:hover {
  background-color: var(--danger-bg);
  transform: translateY(-1px);
}

/* ── Tooltip ── */
.tooltip-bottom {
  position: relative;
}

.tooltip-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(-2px);
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}

.tooltip-bottom:hover::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
  transition-delay: 0.8s;
}

/* ✅ body no padrão */
.card-content-grid {
  padding: 1rem;
}

.details-col {
  width: 100%;
}

/* ── Linha do título + botões à direita ── */
.title-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 12px;
}

/* ✅ título no "peso" do TextCardModal */
.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

/* ✅ BLOCKQUOTE com scroll e borda sutil */
.description-quote {
  margin: 6px 0 0;
  padding: 0.25rem 0 0.25rem 0.75rem;

  max-height: 140px;
  overflow-y: auto;

  border-left: 4px solid var(--brand-soft);
  color: var(--text-light);

  line-height: 1.6;
  white-space: pre-wrap;

  background: transparent;
  border-radius: 0;
}

.description-quote-text {
  font-size: 0.9rem;
}

.description-quote::-webkit-scrollbar {
  width: 10px;
}
.description-quote::-webkit-scrollbar-thumb {
  background: var(--neutral-400);
  border-radius: 999px;
  border: 3px solid transparent;
  background-clip: padding-box;
}
.description-quote::-webkit-scrollbar-track {
  background: transparent;
}

/* ✅ cores em UMA LINHA (sem cortar texto) */
.meta-value-colors {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;

  flex-wrap: nowrap;
  white-space: nowrap;
}

/* cada conjunto Texto / Fundo */
.color-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* ✅ PREVIEW MAIOR */
.color-dot {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  flex-shrink: 0;
}

.color-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* previews — blockquote poético */
.previews-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.shared-preview-shell {
  width: 100%;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px;
  font-size: 0.9rem;
  padding: 0.45rem 1.1rem 0.35rem;
  box-sizing: border-box;
  background: var(--bg-content);
}

.poem-blockquote {
  margin: 0;
  width: 100%;
  padding-left: 0.55rem;
  border-left: 4px solid #b39aff;
  font-style: italic;
  color: var(--text-dark);
}

.poem-line {
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

/* footer */
.footer-info {
  padding-top: 8px;
  width: 100%;
}

/* ✅ chips lado a lado + wrap */
.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

</style>